import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    model,
    untracked
} from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SiFormItemComponent } from '@siemens/element-ng/form';
import { Kpi } from '@tran-monorepo/interfaces/applications';
import { createKpiSelectorForm, KpiFormArrayComponent, KpiSelectorFormGroup } from '../../form';
import { ApplicationDetailDashboardBoxComponent } from '../application-detail-dashboard-box/application-detail-dashboard-box.component';
import { ApplicationImpactCriteriaForm } from '../application-detail.form';

@Component({
    selector: 'tran-application-influence-and-impact',
    imports: [
        ReactiveFormsModule,
        ApplicationDetailDashboardBoxComponent,
        SiFormItemComponent,
        TranslateModule,
        KpiFormArrayComponent
    ],
    templateUrl: './application-influence-and-impact.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplicationInfluenceAndImpactComponent {
    public readonly heading = input<string>(
        'components.application-detail-dashboard.application-influence-and-impact.heading'
    );
    public readonly isLoading = model<boolean>(false);
    public readonly readonly = input(false, { transform: booleanAttribute });

    public readonly contribution = input<string | null>(null);
    public readonly involvement = input<string | null>(null);
    public readonly transferAndScalability = input<string | null>(null);

    public readonly form = input<ApplicationImpactCriteriaForm | null>(null);
    public readonly selectedKpis = input<Kpi[]>([]);
    public readonly predefinedKpis = input<Kpi[]>([]);

    protected readonly kpiForms = new FormArray<KpiSelectorFormGroup>([]);

    protected readonly localForm: ApplicationImpactCriteriaForm = new FormGroup({
        contribution: new FormControl<string>('', { nonNullable: true }),
        involvement: new FormControl<string>('', { nonNullable: true }),
        transferAndScalability: new FormControl<string>('', { nonNullable: true }),
        kpis: new FormArray<KpiSelectorFormGroup>([])
    }) as ApplicationImpactCriteriaForm;

    protected readonly activeForm = computed(() => this.form() ?? this.localForm);
    protected readonly kpiFormArray = computed(() => this.form()?.controls.kpis ?? this.kpiForms);

    constructor() {
        effect(() => {
            const contribution = this.contribution();
            const involvement = this.involvement();
            const transferAndScalability = this.transferAndScalability();
            const selectedKpis = this.selectedKpis();
            const predefinedKpis = this.predefinedKpis();
            const readonly = this.readonly();

            untracked(() => {
                this.activeForm().patchValue({
                    contribution: contribution ?? '',
                    involvement: involvement ?? '',
                    transferAndScalability: transferAndScalability ?? ''
                });

                if (readonly) {
                    return;
                }

                const targetFormArray = this.form()?.controls.kpis ?? this.kpiForms;
                targetFormArray.clear({ emitEvent: false });

                selectedKpis.forEach(kpi => {
                    const group = createKpiSelectorForm({ validators: Validators.required });
                    const predefinedKpi = kpi.kpiDefinitionId
                        ? predefinedKpis.find(option => option.id === kpi.kpiDefinitionId)
                        : undefined;

                    group.patchValue({
                        selection: predefinedKpi
                            ? [{ type: 'predefined', value: predefinedKpi }]
                            : [{ type: 'custom', value: kpi.name }],
                        definition: kpi.definition ?? kpi.name,
                        startValue: kpi.startValue,
                        startValueUnit: kpi.startUnit?.id ?? '',
                        targetValue: kpi.targetValue,
                        targetValueUnit: kpi.targetUnit?.id ?? ''
                    });
                    targetFormArray.push(group, { emitEvent: false });
                });
            });
        });
    }
}
